import { get, writable } from "svelte/store";

import length from "@turf/length";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { lineString, point } from "@turf/helpers";

import GeoHelper from "../../Helpers/GeoHelper";
import ValidationHelper from "../../Helpers/ValidationHelper";
import StringHelper from "../../Helpers/StringHelper";
import arrayMove from "../../Helpers/Misc/arrayMove";
import roundFloat from "../../Helpers/Misc/roundFloat";

import MapStore from "../../Map/Store";
import AuthStore from "../../Auth/Store";

import MapDataSource from "../../Map/Enums/MapDataSource";

import DistanceUnit from "./DistanceUnit";
import DistanceUtil from "./DistanceUtil";
import Mexp from "math-expression-evaluator/dist/browser/math-expression-evaluator.min.js";

import RangeRingStore from "./RangeRing/Store";

const MeasureStore = {
	meta: {
		lastUpdated: writable(0)
	},
	inputs: {
		tableData: writable([]),
		speedCalc: {
			time: writable("")
		},
		timeCalc: {
			speed: writable("")
		}
	},
	results: {
		distance: {
			km: writable(0),
			miles: writable(0),
			nauticalMiles: writable(0)
		},
		speed: writable(0),
		time: writable(0),
		errs: {
			speed: writable(""),
			time: writable("")
		}
	},
	actions: {

	},
	helpers: {

	}
};

MeasureStore.actions.addPoint = () => {
	if (get(MeasureStore.inputs.tableData).length === 0) {
		// Add at center of current map view.
		const realCenter = MapStore.helpers.getCenterLngLat();
		MeasureStore.actions.addPointAt(realCenter.lng, realCenter.lat);
	}
	else {
		// Add next to most recent point (taking into account map view/scale)
		const tableData = get(MeasureStore.inputs.tableData);
		const mostRecentPoint = tableData[tableData.length - 1];
		const arr = GeoHelper.parseStringLngLat(mostRecentPoint.lngLat);

		const map = MapStore.helpers.getMap();
		const bounds = map.getBounds(); // LngLatBounds
		const cameraWidth = bounds.getEast() - bounds.getWest();

		MeasureStore.actions.addPointAt(parseFloat(arr[0]) + (parseFloat(cameraWidth) * 0.02), arr[1]);
	}
};

// Add point onto the end of the list.
MeasureStore.actions.addPointAt = (lng, lat) => {
	const user = get(AuthStore.session.user);
	const lngLatFormat = user.lngLatFormat;

	const newPoint = {
		lngLat: GeoHelper.lngLatCoordinatesToString([lng, lat], lngLatFormat, 4), // roundFloat(lng, 4) + ", " + roundFloat(lat, 4),
		lng: lng,
		lat: lat,
		selected: false
	};
	let tableData = get(MeasureStore.inputs.tableData);
	tableData = [
		...tableData,
		newPoint
	];
	MeasureStore.inputs.tableData.set(tableData);

	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};

// Find the closest two points, and add a point between them, separating the lines.
MeasureStore.actions.addPointBetweenAt = (lng, lat) => {
	const user = get(AuthStore.session.user);
	const lngLatFormat = user.lngLatFormat;

	const newPoint = {
		lngLat: GeoHelper.lngLatCoordinatesToString([lng, lat], lngLatFormat, 4), // roundFloat(lng, 4) + ", " + roundFloat(lat, 4),
		lng: lng,
		lat: lat,
		selected: false
	};

	let tableData = get(MeasureStore.inputs.tableData);

	const line = lineString(MeasureStore.helpers.toPoints(tableData));
	const targetPoint = point([lng, lat]);

	const snapped = nearestPointOnLine(line, targetPoint, { units: "miles" });
	const index = snapped.properties.index + 1;

	tableData.splice(index, 0, newPoint);
	tableData = [...tableData];

	MeasureStore.inputs.tableData.set(tableData);

	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};

MeasureStore.actions.removePointByLngLat = (lng, lat) => {
	const index = MeasureStore.helpers.findPointByLngLat(lng, lat);
	if (index >= 0) {
		let tableData = get(MeasureStore.inputs.tableData);
		tableData.splice(index, 1);
		tableData = [...tableData]; // Svelte requires a = to detect changes.
		MeasureStore.inputs.tableData.set(tableData);
	}

	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};

MeasureStore.actions.movePointPrev = (lng, lat) => {
	const index = MeasureStore.helpers.findPointByLngLat(lng, lat);
	if (index > 0) {
		MeasureStore.actions.movePoint(index, index - 1);
	}
};

MeasureStore.actions.movePointNext = (lng, lat) => {
	const index = MeasureStore.helpers.findPointByLngLat(lng, lat);
	if (index < get(MeasureStore.inputs.tableData).length - 1) {
		MeasureStore.actions.movePoint(index, index + 1);
	}
};

MeasureStore.actions.movePoint = (indexFrom, indexTo) => {
	let tableData = get(MeasureStore.inputs.tableData);
	arrayMove(tableData, indexFrom, indexTo);
	tableData = [...tableData];
	MeasureStore.inputs.tableData.set(tableData);

	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};

MeasureStore.actions.editPoint = (index, lngLatObj = { lng: 0, lat: 0 }) => {
	const user = get(AuthStore.session.user);
	const lngLatFormat = user.lngLatFormat;
	let tableData = get(MeasureStore.inputs.tableData);
	tableData[index].lng = lngLatObj.lng;
	tableData[index].lat = lngLatObj.lat;
	tableData[index].lngLat = GeoHelper.lngLatCoordinatesToString([lngLatObj.lng, lngLatObj.lat], lngLatFormat, 4); // roundFloat(lngLatObj.lng, 4) + ", " + roundFloat(lngLatObj.lat, 4);
	tableData = [...tableData];
	MeasureStore.inputs.tableData.set(tableData);

	// Rebuild geojson
	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};
MeasureStore.actions.adjustPoint = (lngLatCoords = { lng: 0, lat: 0 }) => {
	const index = MeasureStore.helpers.findClosestPointByLngLat(lngLatCoords.lng, lngLatCoords.lat);

	// Move the point in the table data.
	if (index >= 0) {
		MeasureStore.actions.editPoint(index, lngLatCoords);
	}
};

MeasureStore.actions.clearAllPoints = () => {
	MeasureStore.inputs.tableData.set([]);

	MeasureStore.actions.recalculateDistance();
	MeasureStore.helpers.onLineUpdated();
	MeasureStore.actions.update();
};

MeasureStore.actions.update = () => {
	MeasureStore.meta.lastUpdated.set(Date.now());
};

MeasureStore.actions.recalculateDistance = () => {
	const tableData = get(MeasureStore.inputs.tableData);
	if (tableData.length >= 2) {
		const points = MeasureStore.helpers.toPoints(tableData);
		const line = lineString(points);
		const lenKM = length(line, { units: DistanceUnit.KILOMETERS });
		const lenMiles = length(line, { units: DistanceUnit.MILES });

		MeasureStore.results.errs.time.set("");
		MeasureStore.results.errs.speed.set("");
		MeasureStore.results.distance.km.set(lenKM);
		MeasureStore.results.distance.miles.set(lenMiles);
		MeasureStore.results.distance.nauticalMiles.set(DistanceUtil.convert(lenMiles, DistanceUnit.MILES, DistanceUnit.NAUTICAL_MILES));

		let inputTime = get(MeasureStore.inputs.speedCalc.time);
		try {
			if (inputTime.length > 0) {
				inputTime = Mexp.eval(inputTime);
			}
			const speed = MeasureStore.helpers.calculateSpeed(inputTime, DistanceUnit.NAUTICAL_MILES);
			MeasureStore.results.speed.set(speed);
		}
		catch (e) {
			MeasureStore.results.errs.speed.set(e.message);
		}

		let inputSpeed = get(MeasureStore.inputs.timeCalc.speed);
		try {
			if (inputSpeed.length > 0) {
				inputSpeed = Mexp.eval(inputSpeed);
			}
			const time = MeasureStore.helpers.calculateTime(inputSpeed, DistanceUnit.NAUTICAL_MILES);
			MeasureStore.results.time.set(time);
		}
		catch (e) {
			MeasureStore.results.errs.time.set(e.message);
		}
	}
	else {
		MeasureStore.results.errs.time.set("");
		MeasureStore.results.time.set("");

		MeasureStore.results.errs.speed.set("");
		MeasureStore.results.speed.set("");
	}
};

MeasureStore.helpers.onLineUpdated = () => {
	MeasureStore.helpers.rebuildGeoJSON();
};

MeasureStore.helpers.rebuildGeoJSON = () => {
	const tableData = get(MeasureStore.inputs.tableData);
	const points = MeasureStore.helpers.toPoints(tableData);
	const featurePoints = GeoHelper.featureCollectionPoints(points);
	const featureLines = GeoHelper.featureCollectionLineString(points);
	featureLines.features[0].properties.color = "black";
	const featureLabels = GeoHelper.featureCollectionPoints([]);

	// Build range ring.
	if (tableData.length === 1) {
		for (let i = 0; i < get(RangeRingStore.items).length; i++) {
			const arc = get(get(RangeRingStore.items)[i].result.arc);
			if (arc) {
				featureLines.features.push(arc);
			}
			const label = get(get(RangeRingStore.items)[i].result.label);
			if (label) {
				featureLabels.features.push(label);
			}
		}
	}

	// Set lines on map
	const map = MapStore.helpers.getMap();
	const mapInitialised = get(MapStore.meta.initialised);
	if (map && mapInitialised) {
		map.getSource(MapDataSource.MEASURE_POINTS).setData(featurePoints);
		map.getSource(MapDataSource.MEASURE_POINT_LINES).setData(featureLines);
		map.getSource(MapDataSource.MEASURE_LABELS).setData(featureLabels);
	}
};

MeasureStore.helpers.findPointByLngLat = (lng, lat) => {
	const tableData = get(MeasureStore.inputs.tableData);
	for (let i = 0; i < tableData.length; i++) {
		if (tableData[i].lng === lng && tableData[i].lat === lat) {
			return i;
		}
	}
	return -1;
};

MeasureStore.helpers.findClosestPointByLngLat = (lng, lat) => {
	const tableData = get(MeasureStore.inputs.tableData);
	let closestPointIndex = null;
	let closestPointDistanceSq = 999999999;
	for (let i = 0; i < tableData.length; i++) {
		const distSq = DistanceUtil.distanceSquared(lng, lat, tableData[i].lng, tableData[i].lat);
		if (distSq < closestPointDistanceSq) {
			closestPointDistanceSq = distSq;
			closestPointIndex = i;
		}
	}
	return closestPointIndex;
};

MeasureStore.helpers.calculateSpeed = (timeInMinutes, speedUnits) => {
	MeasureStore.results.errs.speed.set("");
	if (("" + timeInMinutes).length === 0) {
		return "";
	}

	try {
		ValidationHelper.isValidNonZeroNumber(timeInMinutes, "Time");
	}
	catch (e) {
		MeasureStore.results.errs.speed.set(e.message);
		return "";
	}

	let useDistance;
	if (speedUnits === DistanceUnit.NAUTICAL_MILES) {
		useDistance = get(MeasureStore.results.distance.nauticalMiles);
	}
	else if (speedUnits === DistanceUnit.KILOMETERS) {
		useDistance = get(MeasureStore.results.distance.km);
	}
	else if (speedUnits === DistanceUnit.MILES) {
		useDistance = get(MeasureStore.results.distance.miles);
	}
	const result = (useDistance / (timeInMinutes / 60));
	return DistanceUtil.addSpeedUnitsString(roundFloat(result, 2), speedUnits);

	// if (("" + timeInMinutes).length === 0) {
	// 	return "";
	// }
	// timeInMinutes = parseFloat(timeInMinutes);
	// if (Number.isNaN(timeInMinutes)) {
	// 	MeasureStore.results.errs.speed.set("Time is invalid (nan)");
	// 	throw new Error("Time is invalid (nan)");
	// 	return "";
	// }
	// else if (!Number.isFinite(timeInMinutes)) {
	// 	MeasureStore.results.errs.speed.set("Time is invalid (infinity)");
	// 	return "";
	// }
	// else if (timeInMinutes === 0) {
	// 	MeasureStore.results.errs.speed.set("Time is invalid (it should not be 0)");
	// 	return "";
	// }

	// let result;
	// if (speedUnits === DistanceUnit.NAUTICAL_MILES) {
	// 	result = (get(MeasureStore.results.distance.nauticalMiles) / (timeInMinutes / 60));
	// }
	// else if (speedUnits === DistanceUnit.KILOMETERS) {
	// 	result = (get(MeasureStore.results.distance.km) / (timeInMinutes / 60));
	// }
	// else if (speedUnits === DistanceUnit.MILES) {
	// 	result = (get(MeasureStore.results.distance.miles) / (timeInMinutes / 60));
	// }

	// if (Number.isNaN(result)) {
	// 	MeasureStore.results.errs.speed.set("Result is NaN");
	// 	return "";
	// }
	// else if (!Number.isFinite(result)) {
	// 	MeasureStore.results.errs.speed.set("Result is Infinity");
	// 	return "";
	// }
	// return DistanceUtil.addSpeedUnitsString(roundFloat(result, 2), speedUnits);
};

MeasureStore.helpers.calculateTime = (speed, speedUnits) => {
	MeasureStore.results.errs.time.set("");
	if (("" + speed).length === 0) {
		return "";
	}

	try {
		ValidationHelper.isValidNonZeroNumber(speed, "Speed");
	}
	catch (e) {
		MeasureStore.results.errs.time.set(e.message);
		return "";
	}

	if (speedUnits === DistanceUnit.NAUTICAL_MILES) {
		const hours = get(MeasureStore.results.distance.nauticalMiles) / speed;
		return StringHelper.formatHoursAndMinutes(hours);
		// const minutes = (hours % 1) * 60.0;
		// return Math.floor(hours) + " hours " + Math.round(minutes) + " minutes";
	}
	// TODO: other units
	return "";
};

MeasureStore.helpers.toPoints = (tableData) => {
	const lineData = [];
	for (let i = 0; i < tableData.length; i++) {
		// const lngLat = tableData[i].lngLat;
		const lng = tableData[i].lng;
		const lat = tableData[i].lat;
		// const pcs = lngLat.split(",");
		// const lng = pcs[0].trim();
		// const lat = pcs[1].trim();
		lineData.push([lng, lat]);
	}
	return lineData;
};

MeasureStore.inputs.speedCalc.time.subscribe((value) => {
	try {
		if (value.length > 0) {
			value = Mexp.eval(value);
		}
		const speed = MeasureStore.helpers.calculateSpeed(value, DistanceUnit.NAUTICAL_MILES);
		MeasureStore.results.speed.set(speed);
	}
	catch (e) {
		MeasureStore.results.errs.speed.set(e.message);
	}
});
MeasureStore.inputs.timeCalc.speed.subscribe((value) => {
	try {
		if (value.length > 0) {
			value = Mexp.eval(value);
		}
		const time = MeasureStore.helpers.calculateTime(value, DistanceUnit.NAUTICAL_MILES);
		MeasureStore.results.time.set(time);
	}
	catch (e) {
		MeasureStore.results.errs.time.set(e.message);
	}
});

export default MeasureStore;
